import { fg } from '@atlaskit/platform-feature-flags';
import type { Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

import {
	type ConvoAiSkillNode,
	type SkillDirectoryItemNode,
	parseConvoAiSkillEdges,
	parseSkillEdges,
} from '../../utils/parse-skill';

const GRAPHQL_ENDPOINT = '/gateway/api/graphql';

/**
 * Legacy GraphQL query — graphIntegration_componentDirectoryItems.
 * Returns user-facing descriptions (not AI model instructions).
 * Used when rovo_chat_unified_skills_agg is OFF.
 */
const LEGACY_SKILLS_QUERY = `
  query GraphIntegrationItemsQuery(
    $first: Int
    $cloudId: ID!
    $surface: GraphIntegrationSurface
    $relevantFor: [GraphIntegrationDirectoryItemType!]
  ) {
    graphIntegration_componentDirectoryItems(
      first: $first
      cloudId: $cloudId
      surface: $surface
      relevantFor: $relevantFor
    )
    @optIn(to: "GraphIntegrationItemsQuery")
    {
      edges {
        node {
          __typename
          ... on GraphIntegrationSkillDirectoryItem {
            id
            slashCommand
            name
            description
            iconUrl
            integrationKey {
              displayName
              iconUrl
            }
            color
            tags {
              displayName
            }
          }
        }
      }
    }
  }
`;

/**
 * New unified GraphQL query — convoai_availableSkills.
 * Used when rovo_chat_unified_skills_agg is ON.
 */
const UNIFIED_SKILLS_QUERY = `
  query ConvoAiAvailableSkillsQuery(
    $first: Int
    $cloudId: ID!
  ) {
    convoai_availableSkills(
      first: $first
      cloudId: $cloudId
    )
    @optIn(to: "ConvoAiAvailableSkills")
    {
      edges {
        node {
          __typename
          ... on ConvoAiSkill {
            id
            name
            description
            displayName
            slashCommand
						helpText
            iconUrl
            iconKey
            color
            source
            tags
            category
            integrationKeys
          }
        }
      }
      errors {
        message
      }
    }
  }
`;

interface LegacyGraphQLResponse {
	data?: {
		graphIntegration_componentDirectoryItems?: {
			edges?: ReadonlyArray<{
				node?: SkillDirectoryItemNode | null;
			} | null>;
		} | null;
	};
	errors?: Array<{ message: string }>;
}

interface UnifiedGraphQLResponse {
	data?: {
		convoai_availableSkills?: {
			edges?: ReadonlyArray<{
				node?: ConvoAiSkillNode | null;
			} | null>;
			errors?: Array<{ message: string }> | null;
		} | null;
	};
	errors?: Array<{ message: string }>;
}

/**
 * Session-level cache for fetched skills.
 * Keyed by `${cloudId}:${graphqlEndpoint}:${useUnified}` to avoid redundant network requests.
 * Skills are cached for the lifetime of the page (matching Relay store behavior).
 * A page refresh will fetch fresh data.
 */
const skillsCache = new Map<string, Skill[]>();

/**
 * Clears the skills cache. Useful for testing or forcing a refresh.
 */
export function clearSkillsCache(): void {
	skillsCache.clear();
}

/**
 * Fetch skills using the new unified convoai_availableSkills endpoint.
 */
async function fetchSkillsUnified(cloudId: string): Promise<Skill[]> {
	const response = await fetch(GRAPHQL_ENDPOINT, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'include',
		body: JSON.stringify({
			query: UNIFIED_SKILLS_QUERY,
			variables: { cloudId, first: 100 },
		}),
	});

	if (!response.ok) {
		return [];
	}

	const json: UnifiedGraphQLResponse = await response.json();

	if (json.errors?.length || json.data?.convoai_availableSkills?.errors?.length) {
		return [];
	}

	const edges = json.data?.convoai_availableSkills?.edges;
	return parseConvoAiSkillEdges(edges);
}

/**
 * Fetch skills using the legacy graphIntegration_componentDirectoryItems endpoint.
 */
async function fetchSkillsLegacy(cloudId: string): Promise<Skill[]> {
	const response = await fetch(GRAPHQL_ENDPOINT, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'include',
		body: JSON.stringify({
			query: LEGACY_SKILLS_QUERY,
			variables: {
				cloudId,
				first: 100,
				surface: 'ROVO',
				relevantFor: ['SKILL'],
			},
		}),
	});

	if (!response.ok) {
		return [];
	}

	const json: LegacyGraphQLResponse = await response.json();

	if (json.errors?.length) {
		return [];
	}

	const edges = json.data?.graphIntegration_componentDirectoryItems?.edges;
	return parseSkillEdges(edges);
}

/**
 * Fetch skills from the GraphQL API.
 *
 * Uses vanilla `fetch` (not Relay) so it can be called outside React
 * in the extension manifest setup.
 *
 * When `rovo_chat_unified_skills_agg` is ON, uses the new `convoai_availableSkills`
 * endpoint. Otherwise, falls back to the legacy `graphIntegration_componentDirectoryItems`
 * endpoint which returns user-facing descriptions.
 *
 * Results are cached for the lifetime of the page (session-level cache,
 * matching Relay store behavior). A page refresh will fetch fresh data.
 *
 * @param cloudId - The Atlassian cloud ID for the site
 */
export async function fetchSkills(cloudId: string): Promise<Skill[]> {
	const useUnified = fg('rovo_chat_unified_skills_agg');
	const cacheKey = `${cloudId}:${useUnified}`;
	const cached = skillsCache.get(cacheKey);
	if (cached) {
		return cached;
	}

	try {
		const skills = useUnified
			? await fetchSkillsUnified(cloudId)
			: await fetchSkillsLegacy(cloudId);

		skillsCache.set(cacheKey, skills);
		return skills;
	} catch {
		// Silently fail — the insert menu will just not show skills
		return [];
	}
}
